import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import FlowDiagram from "@/components/FlowDiagram";
import AuditCta from "@/components/AuditCta";
import ClosingCta from "@/components/sections/ClosingCta";
import { WORKFLOWS, getWorkflow } from "@/config/workflows";

export function WorkflowLibraryPage() {
  return (
    <main data-testid="workflow-library-page">
      <Seo
        title="Workflow Library"
        description="A library of AI sales workflows we build for Singapore businesses: instant replies, follow-up, reminders, lead routing and weekly pipeline reports."
        path="/workflows"
      />
      <PageHero
        label="Workflow Library"
        headline="The workflows we build most often."
        subline="Each one is a real workflow with a trigger, a few steps and a result. Take any of them as they are, or as the starting point for yours."
      />
      <section className="px-6 sm:px-8 pb-20 sm:pb-28">
        <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-5">
          {WORKFLOWS.map((w) => (
            <Link
              key={w.slug}
              to={`/workflows/${w.slug}`}
              className="card-lift bg-[#EADCC6] rounded-2xl border border-[#1F1F1C]/10 p-7 flex flex-col"
              data-testid={`workflow-card-${w.slug}`}
            >
              <h2 className="font-fraunces font-extrabold text-base md:text-lg text-[#1F1F1C] leading-snug">{w.title}</h2>
              <p className="mt-3 text-sm text-[#4A463F] leading-relaxed flex-1">{w.summary}</p>
              <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#C8703A]">
                See the workflow <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.5} />
              </span>
            </Link>
          ))}
        </div>
      </section>
      <ClosingCta testId="workflow-library-closing-cta" />
    </main>
  );
}

export default function WorkflowPage() {
  const { slug } = useParams();
  const workflow = getWorkflow(slug);

  if (!workflow) return <Navigate to="/workflows" replace />;

  return (
    <main data-testid={`workflow-page-${workflow.slug}`}>
      <Seo
        title={workflow.title}
        description={`${workflow.summary} Built by Raki Solutions in Singapore, in your own accounts.`}
        path={`/workflows/${workflow.slug}`}
      />
      <PageHero label="Workflow" headline={workflow.title} subline={workflow.summary} />

      <section className="px-6 sm:px-8 pb-16" data-testid="workflow-diagram-section">
        <div className="max-w-4xl mx-auto bg-[#EADCC6] rounded-2xl border border-[#1F1F1C]/10 p-7 sm:p-9">
          <p className="text-xs uppercase tracking-[0.18em] font-semibold text-[#4A463F] mb-6">How it runs</p>
          <FlowDiagram steps={workflow.steps} testId="workflow-diagram" />
        </div>
      </section>

      <section className="px-6 sm:px-8 pb-20 sm:pb-24">
        <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-10">
          <div data-testid="workflow-who">
            <h2 className="font-fraunces font-extrabold text-xl sm:text-2xl tracking-tight text-[#1F1F1C]">Who it is for</h2>
            <ul className="mt-6 space-y-3">
              {workflow.who.map((w) => (
                <li key={w} className="flex gap-3 text-sm text-[#4A463F] leading-relaxed">
                  <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#C8703A] shrink-0" aria-hidden="true" />
                  {w}
                </li>
              ))}
            </ul>
          </div>
          <div data-testid="workflow-tools">
            <h2 className="font-fraunces font-extrabold text-xl sm:text-2xl tracking-tight text-[#1F1F1C]">Tools it can use</h2>
            <div className="mt-6 flex flex-wrap gap-2">
              {workflow.tools.map((t) => (
                <span key={t} className="rounded-2xl border border-[#1F1F1C]/20 px-4 py-2 text-sm text-[#1F1F1C]">
                  {t}
                </span>
              ))}
            </div>
            <p className="mt-5 text-sm text-[#4A463F] leading-relaxed">
              We recommend what fits your stack and costs the least. Everything runs in your accounts, in your name.
            </p>
          </div>
        </div>
      </section>

      <section className="px-6 sm:px-8 pb-20 sm:pb-28" data-testid="workflow-cta-section">
        <div className="max-w-4xl mx-auto bg-[#EADCC6] rounded-2xl border border-[#1F1F1C]/10 p-8 sm:p-10">
          <h2 className="font-fraunces font-extrabold text-2xl sm:text-3xl tracking-tight text-[#1F1F1C]">
            Want this in your business?
          </h2>
          <div className="mt-7 flex flex-col sm:flex-row gap-4">
            <AuditCta testId="workflow-page-cta" />
            <Link
              to="/workflows"
              className="btn-press inline-flex items-center justify-center rounded-2xl border border-[#1F1F1C]/25 px-7 py-4 font-semibold text-sm sm:text-base text-[#1F1F1C] hover:border-[#C8703A] hover:text-[#C8703A]"
              data-testid="workflow-back-to-library"
            >
              Back to the library
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
